import moment from 'moment';
import { findIndex } from 'lodash';

import {
  longDateWithWeekday,
  formatDateTimeWithTimezone,
  timeFromNow,
  CALENDAR_DATE_FORMAT,
  SHORT_DATE_FORMAT,
  TWENTY_FOUR_HOUR_TIME_FORMAT,
} from '../../../utils/Date';

moment.updateLocale('en', {
  week: {
    dow: 1, // Monday is the first day of the week.
  },
});

export const orderedTimesheets = (timesheets, orderedTimesheetIds) => {
  const mappedTimesheets = [];

  orderedTimesheetIds.forEach((timesheetId) => {
    const timesheet = timesheets[timesheetId];
    const {
      belongsToCurrentUser,
      jobOwner,
      firstEntryDate,
      lastEntryDate,
      submittedAt,
      approvedAt,
      subtotal,
      subtotalGst,
      ordinaryHours,
      penaltyHours,
      jobTimezone,
      rejectionState,
    } = timesheet;

    const timesheetPeriod = `${formatDateTimeWithTimezone(firstEntryDate, SHORT_DATE_FORMAT, jobTimezone)} - ${formatDateTimeWithTimezone(lastEntryDate, SHORT_DATE_FORMAT, jobTimezone)}`;

    mappedTimesheets.push({
      ...timesheet,
      timesheetPeriod,
      isApproved: Boolean(approvedAt),
      isRejected: rejectionState === 'REJECTED',
      isResolved: rejectionState === 'RESOLVED',
      submittedAgo: timeFromNow(submittedAt),
      approvedAgo: timeFromNow(approvedAt),
      total: subtotal + subtotalGst,
      jobOwner: belongsToCurrentUser ? 'You' : jobOwner,
      totalHours: ordinaryHours + penaltyHours,
    });
  });

  return mappedTimesheets;
};

const parseTimesheetEntry = (timesheetEntry, timezone) => {
  const {
    startTime,
    finishTime,
    totalHours,
    timesheetBreaks,
  } = timesheetEntry;

  const breakPeriods = timesheetBreaks.map(({ startTime: breakStartTime, finishTime: breakFinishTime }) => (
    `${formatDateTimeWithTimezone(breakStartTime, TWENTY_FOUR_HOUR_TIME_FORMAT, timezone)} - ${formatDateTimeWithTimezone(breakFinishTime, TWENTY_FOUR_HOUR_TIME_FORMAT, timezone)}`
  ));

  return {
    breakPeriods,
    date: longDateWithWeekday(startTime),
    hours: totalHours,
    timesheetEntryPeriod: `${formatDateTimeWithTimezone(startTime, TWENTY_FOUR_HOUR_TIME_FORMAT, timezone)} - ${formatDateTimeWithTimezone(finishTime, TWENTY_FOUR_HOUR_TIME_FORMAT, timezone)}`,
  };
};

const groupTimesheetEntriesByWeek = (timesheetEntries, timezone) => {
  const sortedTimesheetEntries = timesheetEntries.sort((timesheetEntryA, timesheetEntryB) => moment(timesheetEntryA.startTime) - moment(timesheetEntryB.startTime));

  const groupedTimesheets = sortedTimesheetEntries.reduce((acc, timesheetEntry) => {
    const startOfWeek = moment(timesheetEntry.startTime).startOf('week');
    const endOfWeek = moment(timesheetEntry.startTime).endOf('week');
    const weekPeriod = `${formatDateTimeWithTimezone(startOfWeek, SHORT_DATE_FORMAT, timezone)} - ${formatDateTimeWithTimezone(endOfWeek, SHORT_DATE_FORMAT, timezone)}`;

    const weekPeriodIndex = findIndex(acc, ['period', weekPeriod]);

    if (weekPeriodIndex === -1) {
      acc.push({
        period: weekPeriod,
        entries: [parseTimesheetEntry(timesheetEntry, timezone)],
      });
    } else {
      acc[weekPeriodIndex].entries.push(parseTimesheetEntry(timesheetEntry, timezone));
    }

    return acc;
  }, []);

  return groupedTimesheets;
};

export const mapOpenTimesheet = (timesheet) => {
  const {
    timesheetEntries: payslipItems,
    firstEntryDate,
    lastEntryDate,
    jobTimezone,
    submittedAt,
    approvedAt,
    approvedBy,
    rejectionState,
  } = timesheet;

  const startOfInvoice = moment(firstEntryDate).startOf('week');
  const endOfInvoice = moment(lastEntryDate).endOf('week');
  const invoicePeriod = `${formatDateTimeWithTimezone(startOfInvoice, SHORT_DATE_FORMAT, jobTimezone)} - ${formatDateTimeWithTimezone(endOfInvoice, SHORT_DATE_FORMAT, jobTimezone)}`;
  const submittedAtDetails = `Submitted ${formatDateTimeWithTimezone(submittedAt, CALENDAR_DATE_FORMAT, jobTimezone)} at ${formatDateTimeWithTimezone(submittedAt, TWENTY_FOUR_HOUR_TIME_FORMAT, jobTimezone)}`;
  const approvedAtDetails = approvedAt ? `Approved by ${approvedBy} ${formatDateTimeWithTimezone(approvedAt, CALENDAR_DATE_FORMAT, jobTimezone)} at ${formatDateTimeWithTimezone(approvedAt, TWENTY_FOUR_HOUR_TIME_FORMAT, jobTimezone)}` : '';

  const mappedTimesheet = {
    ...timesheet,
    invoicePeriod,
    timesheetEntries: groupTimesheetEntriesByWeek(payslipItems, jobTimezone),
    submittedAtDetails,
    isApproved: Boolean(approvedAt),
    isRejected: rejectionState === 'REJECTED',
    isResolved: rejectionState === 'RESOLVED',
    approvedAtDetails,
  };

  return mappedTimesheet;
};

export const mapTimesheetsToDownload = (timesheets, timesheetIds) => (
  timesheetIds.map(timesheetId => (
    mapOpenTimesheet(timesheets[timesheetId])
  ))
);

const paymentTermCodeToName = paymentTerm => (
  `${paymentTerm.code.replace('_', ' ')} (${paymentTerm.chargePercentage}% credit surcharge)`
);

export const mapPaymentTerms = paymentTerms => (
  paymentTerms.map(paymentTerm => (
    {
      ...paymentTerm,
      name: paymentTermCodeToName(paymentTerm),
    }
  ))
);

export default {
  orderedTimesheets,
  mapOpenTimesheet,
};
